import { displayName, parseIsoToDateOfBirth } from '../../../lib/dependants/helpers';
import { createEmptyPendingDependant } from '../OnboardingContext';

import type { IDependantFormValues } from './types';
import type { CaregiverRelationshipKind } from '../../../__generated__/globalTypes';
import type { IDependant } from '../../../hooks/queries/useDependants';
import type { DependantCount, IPendingDependant } from '../OnboardingContext';

/**
 * Convert a persisted dependant row into form-values shape. Used to seed
 * pending state on resume (count step) and to render edit forms (entry step)
 * — both paths need the same lossless server → form mapping.
 */
export const dependantToFormValues = (dep: {
  relationship: CaregiverRelationshipKind;
  isStep: boolean;
  isInLaw: boolean;
  firstName: string;
  lastName: string;
  preferredName: string | null;
  dateOfBirth: string | null;
}): IDependantFormValues => ({
  relationship: dep.relationship,
  isStep: dep.isStep,
  isInLaw: dep.isInLaw,
  firstName: dep.firstName,
  lastName: dep.lastName,
  preferredName: dep.preferredName ?? '',
  dateOfBirth: parseIsoToDateOfBirth(dep.dateOfBirth),
});

/**
 * Build `count` pending slots, seeding the first `count` from existing server
 * dependants (carrying their `serverId` / `caregiverRelationshipId` so the
 * review step takes the update path, not a duplicate create) and padding any
 * remainder with empty slots. Used to hydrate `pendingDependants` on entry to
 * the loved-ones flow from any direction — forward, resume, or back-nav.
 */
export const seedPendingDependantsFromServer = (
  dependants: ReadonlyArray<IDependant>,
  count: DependantCount
): IPendingDependant[] =>
  Array.from({ length: count }, (_, i) => {
    const dep = dependants[i];
    if (!dep) {
      return createEmptyPendingDependant();
    }
    return {
      values: dependantToFormValues(dep),
      serverId: dep.id,
      caregiverRelationshipId: dep.caregiverRelationshipId,
    };
  });

/**
 * Grow or shrink an array to length `count`. Used when the count step changes
 * how many dependants are being collected — preserve filled-out forms when
 * possible, fill new slots with `fill()`.
 */
export const truncateForCount = <T>(arr: T[], count: number, fill: () => T): T[] => {
  if (arr.length === count) {
    return arr;
  }
  if (arr.length > count) {
    return arr.slice(0, count);
  }
  const next = [...arr];
  while (next.length < count) {
    next.push(fill());
  }
  return next;
};

/**
 * "Review details for X and Y" title — falls back when names are missing.
 * Accepts a structural shape so both pending-form values (`IDependantFormValues`)
 * and persisted server rows (`IDependant`) flow through one helper.
 */
export const buildReviewTitle = (
  dependants: ReadonlyArray<{ firstName: string; preferredName?: string | null }>
): string => {
  if (dependants.length === 0) {
    return 'Review your loved ones';
  }
  const names = dependants.map(displayName).filter((n) => n.length > 0);
  if (names.length === 0) {
    return 'Review your loved ones';
  }
  if (names.length === 1) {
    return `Review details for ${names[0]}`;
  }
  return `Review details for ${names[0]} and ${names[1]}`;
};
